import { ButtonLink } from "../components/Button";
import { Container } from "../components/Container";
import { HOME_SECTIONS } from "../lib/routes";
import { formatPercentOff } from "../lib/format";
import styles from "./WhatIsDealbid.module.css";
import type { BoardResponse } from "../api/types";
import type { Resource } from "../hooks/useApiResource";

/**
 * What Dealbid is — the canvas's explanation panel, for the visitor who does not already
 * know.
 *
 * An elevated card with a kicker, a 32px title, a lede, two actions and a row of figures.
 * This was the page's hero until `WEB-006` demoted it below the board: canon `AGENTS.md`
 * § 11 and `contracts/DESIGN-TOKENS.md` both rank deal value first, and an introduction
 * above the board pushed the deals off the fold.
 *
 * **The figures are today's real board, not the canvas's.** The canvas fills them with
 * "1,240 deals listed to date", "$2.1M saved by buyers" and "312k qualified clicks" — round
 * marketing numbers standing in for a shape. Canon § 11 rules out invented figures, and
 * Dealbid does not measure money saved at all, so the panel keeps the canvas's three-figure
 * shape and fills it from `GET /v1/board/today`. Until the board has loaded, no figure is
 * shown.
 */
export function WhatIsDealbid({ resource }: { resource: Resource<BoardResponse> }) {
  const board = resource.state.status === "ready" ? resource.state.data : null;

  const percentages =
    board?.top10
      .map((entry) => entry.deal.discountPercent)
      .filter((percent): percent is number => percent !== null) ?? [];
  const bestDiscount = percentages.length === 0 ? null : Math.max(...percentages);

  return (
    <section className={styles.band} aria-labelledby="what-is-dealbid">
      <Container>
        <div className={styles.panel}>
          <p className={styles.kicker}>A daily software-deal marketplace</p>
          <h2 id="what-is-dealbid" className={styles.title}>
            What is Dealbid?
          </h2>
          <p className={styles.lede}>
            A dated, ranked deal board that resets at 00:00 UTC. Founders bid in a daily
            auction for tomorrow&rsquo;s Top 10 placement; buyers come to see today&rsquo;s
            deals. Placement is bought — credibility isn&rsquo;t, which is why the board says
            so right where the ranking is. No account and no checkout here: you open a deal on
            the seller&rsquo;s own site.
          </p>

          <div className={styles.actions}>
            <ButtonLink href={HOME_SECTIONS.today}>Browse today&rsquo;s Top 10 ↑</ButtonLink>
            <ButtonLink href={HOME_SECTIONS.tomorrow} variant="secondary">
              Bid for tomorrow&rsquo;s board
            </ButtonLink>
          </div>

          {board === null ? null : (
            <dl className={styles.stats}>
              <div className={styles.stat}>
                <dt className={styles.statLabel}>positions filled today</dt>
                <dd className={`${styles.statFigure} figure`}>
                  {String(board.capacity.filled)} of {String(board.capacity.top10)}
                </dd>
              </div>

              {bestDiscount === null ? null : (
                <div className={styles.stat}>
                  <dt className={styles.statLabel}>best discount on the board</dt>
                  <dd className={`${styles.statFigure} figure`}>
                    {formatPercentOff(bestDiscount)}
                  </dd>
                </div>
              )}

              <div className={styles.stat}>
                <dt className={styles.statLabel}>Honorable Mentions today</dt>
                <dd className={`${styles.statFigure} figure`}>
                  {String(board.honorableMentions.length)}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </Container>
    </section>
  );
}
